import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useApi,
  ApiError,
  type ViewPreferenceListResponse,
  type ViewPreferenceRow,
} from '../api/client';
import './ManagerViews.css';

/**
 * Manager Settings → Staff views editor at /manager/views.
 *
 * Each row is a kaimahi we know about. Manager toggles which home-page
 * tiles that kaimahi sees by default. Per Sam (2026-05-24): managers can
 * only edit settings for kaimahi at a LOWER role level than their own —
 * exec-manager / administrator / super-admin rows are read-only (visually
 * dimmed, no clickable controls).
 *
 * Server enforces the same rule on PUT — the UI's editable flag is hint
 * only. Permission: team.reassign.
 */

function extractApiError(err: unknown, fallback: string): string {
   
  console.error('[manager-views]', err);
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | null;
    if (body && typeof body.error === 'string') return body.error;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function ManagerViews() {
  const { t } = useTranslation();
  const callApi = useApi();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; data: ViewPreferenceListResponse }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setState({ kind: 'loading' });
    try {
      const data = await callApi<ViewPreferenceListResponse>('/manager/views');
      setState({ kind: 'ok', data });
    } catch (err) {
      setState({ kind: 'error', message: extractApiError(err, 'Failed to load') });
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggleTile(row: ViewPreferenceRow, tileId: string, hide: boolean) {
    if (!row.editable) return;
    const next = hide
      ? [...row.hidden_tiles.filter((t) => t !== tileId), tileId]
      : row.hidden_tiles.filter((t) => t !== tileId);
    setBusy(row.kaimahi_oid);
    try {
      await callApi(`/manager/views/${row.kaimahi_oid}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ hidden: next }),
      });
      await load();
    } catch (err) {
      window.alert(extractApiError(err, 'Failed to save'));
    } finally {
      setBusy(null);
    }
  }

  if (state.kind === 'loading') {
    return (
      <section className="mv">
        <h1 className="mv__title">{t('staffViews.title')}</h1>
        <p className="mv__status">{t('staffViews.loading')}</p>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="mv">
        <h1 className="mv__title">{t('staffViews.title')}</h1>
        <p className="mv__error">{state.message}</p>
      </section>
    );
  }

  const tiles = state.data.tile_catalogue;

  return (
    <section className="mv">
      <header className="mv__header">
        <h1 className="mv__title">{t('staffViews.title')}</h1>
        <p className="mv__subtitle">{t('staffViews.subtitle')}</p>
      </header>

      <div className="mv__legend">
        <span className="mv__legend-item">
          ✓ {t('staffViews.legendVisible')}
        </span>
        <span className="mv__legend-item mv__legend-item--off">
          ✗ {t('staffViews.legendHidden')}
        </span>
      </div>

      <div className="mv__table-wrapper">
        <table className="mv__table">
          <thead>
            <tr>
              <th className="mv__col-kaimahi">{t('staffViews.col.kaimahi')}</th>
              {tiles.map((tile) => (
                <th key={tile} className="mv__col-tile">
                  {t(`staffViews.tile.${tile}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {state.data.rows.map((row) => {
              const isBusy = busy === row.kaimahi_oid;
              return (
                <tr
                  key={row.kaimahi_oid}
                  className={`mv__row ${!row.editable ? 'mv__row--readonly' : ''}`}
                >
                  <td>
                    <div className="mv__cell-name">
                      <strong>{row.display_name ?? row.upn ?? row.kaimahi_oid}</strong>
                      <span className={`mv__role mv__role--${row.role_id}`}>
                        {t(`admin.role.${row.role_id}`)}
                      </span>
                    </div>
                  </td>
                  {tiles.map((tile) => {
                    const isHidden = row.hidden_tiles.includes(tile);
                    return (
                      <td key={tile} className="mv__cell-toggle">
                        <button
                          type="button"
                          className={`mv__toggle ${
                            isHidden ? 'mv__toggle--hidden' : 'mv__toggle--visible'
                          }`}
                          disabled={!row.editable || isBusy}
                          onClick={() => void toggleTile(row, tile, !isHidden)}
                          aria-label={
                            isHidden
                              ? t('staffViews.actionShow')
                              : t('staffViews.actionHide')
                          }
                          title={
                            row.editable
                              ? isHidden
                                ? t('staffViews.actionShow')
                                : t('staffViews.actionHide')
                              : t('staffViews.readonlyHint')
                          }
                        >
                          {isHidden ? '✗' : '✓'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mv__footer-hint">{t('staffViews.guardHint')}</p>
    </section>
  );
}

export default ManagerViews;
